/**
 * BlockGenerationModeSelector — SG-A (T7b iii)
 *
 * The strategy-owned block generation mode, as a control (create) and as a
 * read-only readback (a saved strategy).
 *
 * Contract (REMEDIATION-PLAN § SG2 "User decision — strategy-owned generation
 * mode", 2026-09-06):
 *  - the VALUES and the user-facing copy come from ONE place,
 *    `@modules/shared-types` (`STRATEGY_BLOCK_GENERATION_MODE_LABELS`), which is
 *    compile-time pinned to the schema union `Strategy.blockGenerationMode`.
 *    Never re-declare either here;
 *  - the copy explains WHEN A BLOCK STARTS and nothing else — no speed, cost or
 *    quality promise, because none of that is measured;
 *  - `parallel` is the pre-selected value for a new strategy (the historic
 *    behaviour). The eventual product default remains a qualification decision,
 *    so no copy calls it "recommended";
 *  - absent on a saved strategy resolves to an explicit `parallel` readback
 *    (AC-SG-12); a stored value that is not one of the two is SHOWN as broken,
 *    never quietly read as parallel.
 *
 * Design: `.design/strategy-generation-mode/` (DESIGN_BRIEF.md + DECISIONS.md).
 */
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
// Subpath import: the ui-components BARREL pulls the whole library into jest
// (feedback_ui_components_barrel_jest) — this component is unit-tested.
import { ModeSelectionControl } from '@modules/ui-components/forms/fields/ModeSelectionControl';
import {
  STRATEGY_BLOCK_GENERATION_MODES,
  STRATEGY_BLOCK_GENERATION_MODE_LABELS,
  StrategyBlockGenerationModeError,
  resolveBlockGenerationMode,
  type StrategyBlockGenerationMode,
} from '@modules/shared-types';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';

const LEGEND_ID = 'strategy-block-generation-mode-legend';
const HELPER_ID = 'strategy-block-generation-mode-helper';

/**
 * The one honest sentence about what the control does: it persists a setting.
 * Deliberately says nothing about how long a run takes or how good it is.
 */
export const BLOCK_GENERATION_MODE_HELPER_TEXT =
  'Saved with the strategy and used every time its block screens are generated.';

export interface BlockGenerationModeSelectorProps {
  value: StrategyBlockGenerationMode;
  onChange: (mode: StrategyBlockGenerationMode) => void;
  disabled?: boolean;
}

export const BlockGenerationModeSelector: React.FC<BlockGenerationModeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  // Design review round 1 (dark mode): a tint fill + accent border alone left
  // the two cards nearly indistinguishable on violet paper — the strategy-blocks
  // «state contrast» invariant (13). A radio indicator per option carries the
  // state as SHAPE, so it survives both themes and colour-blind vision; the
  // checked one is `primary.main`, non-text, 4.6:1 light / 8:1 dark.
  const options = useMemo(
    () => STRATEGY_BLOCK_GENERATION_MODES.map((mode) => {
      const checked = mode === value;
      return {
        value: mode,
        label: STRATEGY_BLOCK_GENERATION_MODE_LABELS[mode].label,
        description: STRATEGY_BLOCK_GENERATION_MODE_LABELS[mode].description,
        testId: STRATEGY_LIST.formBlockGenerationModeOption(mode),
        icon: checked
          ? <RadioButtonCheckedIcon fontSize="small" sx={{ color: 'primary.main' }} />
          : <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
      };
    }),
    [value],
  );

  // The create dialog is ~1200px wide; a full-bleed option row puts the 12px
  // description at ~110 characters (design review finding). Capped to a
  // readable measure instead.
  return (
    <Box data-testid={STRATEGY_LIST.formBlockGenerationMode} sx={{ maxWidth: 720 }}>
      <Typography
        id={LEGEND_ID}
        variant="body1"
        fontWeight={500}
        color="text.primary"
        sx={{ mb: 1 }}
      >
        Block generation
      </Typography>
      <ModeSelectionControl
        value={value}
        onChange={(next) => onChange(next as StrategyBlockGenerationMode)}
        options={options}
        layout="vertical"
        showIcons
        showDescriptions
        optionLabelVariant="body1"
        buttonVariant="outlined"
        disabled={disabled}
        aria-labelledby={LEGEND_ID}
        aria-describedby={HELPER_ID}
      />
      <Typography
        id={HELPER_ID}
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1 }}
      >
        {BLOCK_GENERATION_MODE_HELPER_TEXT}
      </Typography>
    </Box>
  );
};

export interface BlockGenerationModeReadbackProps {
  /** The SAVED strategy head. `undefined` renders nothing — never a guess. */
  strategy?: { blockGenerationMode?: unknown } | Record<string, unknown> | null;
}

/**
 * Read-only readback of the persisted mode, for surfaces that LAUNCH a run on a
 * saved strategy. Editing the mode after creation is out of scope for SG-A
 * (SG2: "a separate UI decision"), so this is text, never a control.
 */
export const BlockGenerationModeReadback: React.FC<BlockGenerationModeReadbackProps> = ({
  strategy,
}) => {
  if (!strategy) return null;

  let label: string;
  let broken = false;
  try {
    label = STRATEGY_BLOCK_GENERATION_MODE_LABELS[resolveBlockGenerationMode(strategy)].label;
  } catch (error) {
    // A corrupted head must be visible, not silently read as parallel.
    broken = true;
    const stored = (strategy as { blockGenerationMode?: unknown }).blockGenerationMode;
    label = typeof stored === 'string' ? stored : JSON.stringify(stored);
    if (!(error instanceof StrategyBlockGenerationModeError)) throw error;
  }

  return (
    <Box
      data-testid={STRATEGY_LIST.formGenerateConfirmMode}
      sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
    >
      <Typography variant="body1" color="text.secondary">Block generation:</Typography>
      <Typography variant="body1" fontWeight={500} color={broken ? 'error.main' : 'text.primary'}>
        {label}
      </Typography>
      <Typography variant="caption" color={broken ? 'error.main' : 'text.secondary'}>
        {broken ? 'unrecognised saved value — fix it before generating' : 'set when the strategy was created'}
      </Typography>
    </Box>
  );
};

export default BlockGenerationModeSelector;
